import { gql } from 'graphql-request';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { gqlClient } from '@/api';

/**
 * The topics posts are filed under.
 *
 * A table since [Blog 3]; before it, the topic was a free string on each post,
 * which meant renaming one was an edit to every post that used it and a typo
 * made a second topic that looked like the first.
 */
export interface AdminTopic {
  id: string;
  slug: string;
  /** One name per language: `{ en: 'Engineering' }`. */
  name: Record<string, string>;
  position: number;
  /** How many posts are filed under it — counted by the server. */
  posts: number;
}

const ADMIN_TOPICS = gql`
  query AdminTopics {
    adminTopics {
      id
      slug
      name
      position
      posts
    }
  }
`;

export const useAdminTopics = () =>
  useQuery({
    queryKey: ['adminTopics'],
    queryFn: async () =>
      (await gqlClient.request<{ adminTopics: AdminTopic[] }>(ADMIN_TOPICS)).adminTopics,
  });

const CREATE_TOPIC = gql`
  mutation CreateTopic($input: CreateTopicInput!) {
    createTopic(input: $input) {
      id
      slug
      name
      position
      posts
    }
  }
`;

const UPDATE_TOPIC = gql`
  mutation UpdateTopic($id: ID!, $locale: ContentLocale!, $input: UpdateTopicInput!) {
    updateTopic(id: $id, locale: $locale, input: $input) {
      id
      slug
      name
      position
      posts
    }
  }
`;

const TRANSLATE_TOPICS = gql`
  mutation TranslateTopics($ids: [ID!]) {
    translateTopics(ids: $ids) {
      filled
      failed {
        locale
        reason
      }
      topics {
        id
        slug
        name
        position
        posts
      }
    }
  }
`;

const DELETE_TOPIC = gql`
  mutation DeleteTopic($id: ID!, $moveTo: ID) {
    deleteTopic(id: $id, moveTo: $moveTo)
  }
`;

/**
 * Everything the dialog changed is refetched rather than patched in place.
 *
 * The post list carries the topic's name in its own column, and deleting or
 * renaming one changes rows the dialog never saw. Patching the topic cache
 * alone would leave the table behind it showing the old word.
 */
const useTopicMutation = <Input, Result>(run: (input: Input) => Promise<Result>) => {
  const client = useQueryClient();

  return useMutation({
    mutationFn: run,
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['adminTopics'] });
      void client.invalidateQueries({ queryKey: ['adminPosts'] });
    },
  });
};

export const useCreateTopic = () =>
  useTopicMutation(async (input: { name: string; slug: string }) => {
    const data = await gqlClient.request<{ createTopic: AdminTopic }>(CREATE_TOPIC, { input });
    return data.createTopic;
  });

export const useUpdateTopic = () =>
  useTopicMutation(
    async ({
      id,
      locale,
      ...input
    }: {
      id: string;
      locale: string;
      name?: string;
      slug?: string;
    }) => {
      /*
       * Upper-cased on the way out. `ContentLocale` is a GraphQL enum, and its
       * members are EN, VI, JA, KO, ZH — sending 'en' is a validation error
       * from the server, not a value it quietly accepts.
       *
       * The mocked test could not see this: it answers whatever the component
       * sends. It was caught against the running API, which is why the
       * assertion below it now names the wire value.
       */
      const data = await gqlClient.request<{ updateTopic: AdminTopic }>(UPDATE_TOPIC, {
        id,
        locale: locale.toUpperCase(),
        input,
      });
      return data.updateTopic;
    },
  );

/**
 * `moveTo` is where the topic's posts go, and it is absent for a topic with
 * none.
 *
 * The dialog sends the delete without it first, on purpose: the server counts
 * the posts, and its refusal is what tells the screen how many there are to ask
 * about. Counting them here and branching would be a second copy of the rule,
 * and the one that goes stale.
 */
export const useDeleteTopic = () =>
  useTopicMutation(async ({ id, moveTo }: { id: string; moveTo?: string }) => {
    const data = await gqlClient.request<{ deleteTopic: boolean }>(DELETE_TOPIC, {
      id,
      moveTo: moveTo ?? null,
    });
    return data.deleteTopic;
  });

export interface TopicTranslation {
  topics: AdminTopic[];
  /** Names written. */
  filled: number;
  /** Languages that came back with nothing, and why. */
  failed: { locale: string; reason: string }[];
}

/**
 * Fills the missing translations of every topic, or of the ones named —
 * written on the server at once, nothing held here. `ids` omitted means all.
 */
export const useTranslateTopics = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (ids?: string[]) => {
      const data = await gqlClient.request<{ translateTopics: TopicTranslation }>(
        TRANSLATE_TOPICS,
        {
          ids: ids ?? null,
        },
      );
      return data.translateTopics;
    },
    onSuccess: (result) => {
      /*
       * The list the server sent back is put in place at once, then the
       * usual refetch follows. Invalidating alone leaves a gap between the
       * run finishing and the list arriving in which the header still says
       * "Translate 6 missing" — and a second press in that gap is a request
       * the server answers with nothing to do.
       */
      client.setQueryData(['adminTopics'], result.topics);
      void client.invalidateQueries({ queryKey: ['adminTopics'] });
      void client.invalidateQueries({ queryKey: ['adminPosts'] });
    },
  });
};

/** The languages a topic can be named in, English first. */
export const TOPIC_LOCALES = ['en', 'ja', 'ko', 'zh'] as const;
export type TopicLocale = (typeof TOPIC_LOCALES)[number];

/** Languages other than English with no name yet — the ones Translate would fill. */
export const missingLocales = (name: Record<string, string>): TopicLocale[] =>
  TOPIC_LOCALES.filter((locale) => locale !== 'en' && !name[locale]?.trim());

/**
 * The topic's name in English, or in whatever language it was written in.
 *
 * The same fallback the post titles use, and for the same reason: a topic whose
 * English name is empty is not a blank row in the one screen that exists to
 * manage it.
 */
export const topicName = (name: Record<string, string>): string => {
  const english = name.en?.trim();
  if (english) return english;

  const written = Object.values(name).find((value) => value?.trim());
  return written?.trim() ?? 'Untitled';
};

export const slugify = (value: string): string =>
  value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/đ/g, 'd')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
